import {ensureHudStyle} from "./HudSkin.js";
import {copyCoordinatesToClipboard} from "./CopyFeedback.js";

// Permanent, discreet overlay in the bottom-left corner of the 3D viewer,
// showing the point-cloud position currently under the cursor - see
// DigitizationModule's constructor (mounts this into `viewer.renderArea`,
// a sibling of the render canvas, deliberately NOT inside the sidebar's
// scroll area so it stays visible/anchored to the viewport regardless of
// sidebar scroll position) and its mousemove listener (updates it using the
// same point-cloud pick the rest of Digitalisering already performs per
// mousemove - see _pickPlacementPosition() - not a new per-frame query).

// Pure formatting, exported for Level 1 (no-DOM) unit testing - see
// CoordinateInfoBox.test.js. Same precision (3 decimals, no thousands
// separators) as the rest of Digitalisering's coordinate display; `position`
// is a plain {x,y,z} (or null for "no valid pick right now" - shown as an
// explicit neutral state rather than stale leftover numbers, per task
// requirement).
export function formatCoordinateBoxLines(position){
	if(!position){
		return {x: "X   —", y: "Y   —", z: "Z   —"};
	}
	return {
		x: `X   ${position.x.toFixed(3)}`,
		y: `Y   ${position.y.toFixed(3)}`,
		z: `Z   ${position.z.toFixed(3)}`,
	};
}

// container: the DOM element to mount into (viewer.renderArea). Returns
// {update(position|null)} - call on every mousemove-driven pick (or with
// null when the cursor leaves the canvas or isn't over valid point-cloud
// data).
export function createCoordinateInfoBox(container){
	const box = document.createElement('div');
	ensureHudStyle();
	box.className = 'pvh';
	box.style.cssText = [
		'position: absolute',
		'left: 8px',
		// Host apps (Geonote-pointcloud-frontend) dock minimised-window icons
		// (map/video/profile/...) at bottom:20px, ~50-90px tall - 110px keeps
		// this box clear above that dock instead of overlapping/hiding behind
		// it. No such dock exists in Potree's own native UI, so this is purely
		// a "stay above a host app's own bottom-left chrome" margin, not tied
		// to any specific host layout value.
		'bottom: 110px',
		'line-height: 1.6',
		'padding: 6px 10px',
		'z-index: 500',
		'user-select: none',
		'white-space: pre',
		'font-variant-numeric: tabular-nums',
	].join(';');

	const xRow = document.createElement('div');
	const yRow = document.createElement('div');
	const zRow = document.createElement('div');
	box.append(xRow, yRow, zRow);

	container.appendChild(box);

	let current = null;

	function render(){
		const lines = formatCoordinateBoxLines(current);
		xRow.textContent = lines.x;
		yRow.textContent = lines.y;
		zRow.textContent = lines.z;
	}
	render();

	return {
		// The box's own DOM element, so a host app can position it relative to
		// its own chrome (see dock()).
		element: box,
		// Moves the box into `parent` (a host-owned, positioned element) and
		// overrides its default bottom-left placement with `css` (a plain
		// {property: value} style object). Lets a host anchor the box to
		// another overlay (e.g. Geonote's navigation cube) so both move
		// together, without Potree knowing anything about that layout.
		dock(parent, css){
			parent.appendChild(box);
			Object.assign(box.style, css || {});
		},
		update(position){
			current = position || null;
			render();
		},
		// "C"-genvej (DigitizationModule): kopierer boksens AKTUELLE indhold -
		// intet gøres, og false returneres, hvis boksen viser den tomme "—"-
		// tilstand (ingen gyldigt pick lige nu). Genbruger samme
		// formatering/toast som label-kopiikonerne (CopyFeedback.js), så
		// "kopieret"-feedbacken er ens overalt i Digitalisering.
		copyCurrent(){
			if(!current) return false;
			copyCoordinatesToClipboard(current);
			return true;
		},
	};
}
